import React, { useContext, useRef, memo } from "react";
import MaterialTable from "@material-table/core";
import useGetOptionTitle from "../hooks/useGetOptionTitle";
import { tableIcons } from "../ui-components/TableIcons";
import ConfiguratorSurveyRenderResult from "./ConfiguratorSurveyRenderResult";
import {FormattedMessage, useIntl} from "react-intl";
import convertRecursiveRows from "../utils/convertRecursiveRows";
import getRecursiveOutput from "../utils/getRecursiveOutput";
import ConfiguratorContext from "./ConfiguratorContext";
import TableStyle from "./TableStyle";
import ConfiguratorSurveyBlockRender from "./ConfiguratorSurveyBlockRender";
import checkIfBlockIsVisible from "../utils/checkIfBlockIsVisible";

const ConfiguratorSurveyRecursiveQuestionTable = ({
  recursiveQuestionId,
  columns,
  list,
  onChange,
  blocks,
  realtime,
  summary,
  role
}) => {
  const intl = useIntl();
  const getOptionTitle = useGetOptionTitle();
  const context = useContext(ConfiguratorContext);
  const recursiveOutputs = useRef(
    blocks.filter((b) => b.type === "output" && b.output_type === "calculation")
  );
  const lastOutput = useRef(
    recursiveOutputs.current.length
      ? recursiveOutputs.current[recursiveOutputs.current.length - 1]
      : null
  );

  const rows = convertRecursiveRows(list, blocks, getOptionTitle, context, recursiveQuestionId);

  // console.log(rows, lastOutput);
  const activeColumns = rows.reduce(
    (acc, r) => _.uniq([
      ...acc,
      ...Object.entries(r)
        .filter(([k, v]) =>
          (
            (!!v || v === 0)
            && !!blocks.find(({id, type}) => k === `${id}` && type === 'question')
          )
          || false
        )
        .map(([k, _]) => k)
    ]),
    []
  );

  const isKCCOptimization =
    lastOutput.current?.calculation?.length === 1 &&
    lastOutput.current.calculation[0].startsWith('{') &&
    JSON.parse(lastOutput.current.calculation[0]).original === 'kcc_optimization';

  // TODO in case of KCC optimization the total cannot be computed directly: we should retrieve the single configs
  //  totals from the kcc_optimization_results_table, and we should handle the case in which the totals haven't been
  //  already computed (via KK external requests). For now we're just going to hide the total in this particular case
  const total = !isKCCOptimization ? getRecursiveOutput(lastOutput.current, rows) : null;
  const mt = realtime ? "mt-4" : "mt-6";

  const _onRowDelete = (newData) =>
    new Promise((resolve) => {
      setImmediate(() => {
        onChange(list.filter((v, i) => i !== newData.tableData.id))
        resolve()
      });
    });

  const getResponsesToCheck = (current) => {
    if (Object.keys(context?.responses || {}).length === 0)
      return {};
    // substitute to the current recursive question responses the current row only, in order to let the dependencies
    // check work properly
    return {
      // 1. keep all survey responses, since they might be needed in any block inside this recursive question
      ...context.responses,
      // 2. keep the current recursive question, but keep the current row/recursion only
      [recursiveQuestionId]: {
        ...context.responses[recursiveQuestionId],
        recursive_question_value: [current]
      },
      // 3. spread the current recursion responses, since they can be used directly by the blocks inside this recursive
      //    question
      ...current,
    };
  };


  return (
    <div className={`w-full ${mt}`}>
      {!realtime && !summary ? (
        <TableStyle>
          <MaterialTable
            options={{
              paging: false,
              toolbar: false,
              cellStyle: {whiteSpace: "nowrap"},
              headerStyle: {whiteSpace: "nowrap"},
            }}
            localization={{
              header: {actions: ""},
              body: {
                editRow: {deleteText: intl.formatMessage({ id: "delete_element" })}
              }
            }}
            icons={tableIcons}
            columns={columns.filter(i => activeColumns.includes(i['field']))}
            data={rows}
            editable={{
              onRowDelete: _onRowDelete
            }}
          />
        </TableStyle>
      ) : null}
      {realtime || summary ? (
        <div className="flex w-full flex-col">
          {list.map((recursiveResponses, index) => (
            <div
              key={index}
              className={
                index === list.length - 1
                  ? "w-full flex flex-col py-1"
                  : "w-full flex flex-col border-b-2 border-black py-1"
              }
            >
              {blocks
                .filter(
                  (b) =>
                    b.type === "output" &&
                    checkIfBlockIsVisible(b, {
                      ...context,
                      responses: getResponsesToCheck(recursiveResponses),
                    },index)
                )
                .map((output) => (
                  <ConfiguratorSurveyBlockRender
                    key={output.id}
                    recursiveResponses={getResponsesToCheck(recursiveResponses)}
                    recursiveResponsesIndex={index}
                    realtime={realtime}
                    block={output}
                    role={role}
                    summary={summary}
                  />
                ))}
            </div>
          ))}
        </div>
      ) : null}
      {total ? (
        <div className={`flex w-full justify-between ${mt}`}>
          <h2 className="text-2xl">
            <FormattedMessage id="total" defaultMessage="Total" />
          </h2>
          <h2 className="text-2xl">
            <ConfiguratorSurveyRenderResult
              result={total}
              currency={lastOutput.current.output_currency}
            />
          </h2>
        </div>
      ) : null}
    </div>
  );
};

export default memo(ConfiguratorSurveyRecursiveQuestionTable);
